import { createContext, useContext, type ReactNode } from "react";

import { useLocale } from "@superweb/intl";

import { useOnDemandQuery, useQuery } from "@fleet/api";

import { config } from "#internal/config";
import { PageSpinner, useOperationIdContext } from "#internal/ui";
import { stringSearchParamParser, useSearchParams } from "#internal/router";

export const authWithPassport = () => {
  const searchParams = new URLSearchParams({
    retpath: window.location.href,
    origin: "fleet-forms",
  });

  window.location.replace(`${config.passportURL}/auth?${searchParams}`);
};

type AuthContextOptions = {
  authentificationEnabled: boolean;
  info?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
};

const AuthContext = createContext<AuthContextOptions>({} as AuthContextOptions);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const locale = useLocale();
  const [{ ref_id: refId }] = useSearchParams({
    ref_id: stringSearchParamParser<string>(),
    iframe: stringSearchParamParser(),
    skip_vehicle: stringSearchParamParser(),
    skip_license: stringSearchParamParser(),
    specification: stringSearchParamParser(),
  });
  const { sessionId } = useOperationIdContext();

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: {
      ref_id: refId!,
    },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const authentificationEnabled = Boolean(parkInfo?.passport_authorization);

  const { response: suggestResponse, isFetching } = useOnDemandQuery(
    authentificationEnabled
      ? {
          url: "/fleet-forms/selfreg/v1/personal/suggest",
          method: "POST",
          headers: {
            language: locale.toString(),
          },
        }
      : undefined,
  );
  const suggest =
    suggestResponse?.status === 200 ? suggestResponse.body : undefined;

  if (isFetching) {
    return <PageSpinner />;
  }

  return (
    <AuthContext.Provider
      value={{
        authentificationEnabled,
        info: suggest && {
          firstName: suggest.first_name,
          lastName: suggest.last_name,
          phone: suggest.phone,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export const useProfile = () => useContext(AuthContext);
